import type { SupabaseClient } from '../client'
import type { Project, ProjectSummary } from '../domain/project'
import { parseVisibleSections } from '../validators/visible-sections'
import { unwrapList, unwrapSingle } from './_helpers'
import { listAccessibleProjectIds } from './client-project-access'

const PROJECT_COLUMNS =
  'id, name, description, color, avatar_url, health, contract_model, phase, tipo, start_date, end_date, visible_sections, branch, repo_url, contact_name, contract_info, source_deal_id, is_internal, embed_allowed_origins, docs_content, documentation, processes_content, scope_content, created_at, updated_at'

const SUMMARY_COLUMNS = 'id, name, description, color, health, contract_model, phase, tipo'

/**
 * Busca um projeto pelo id. Valida o `visible_sections` via Zod.
 */
export async function getProjectById(client: SupabaseClient, id: number): Promise<Project> {
  const result = await client
    .schema('operacoes')
    .from('projects')
    .select(PROJECT_COLUMNS)
    .eq('id', id)
    .maybeSingle()
  const row = unwrapSingle(result, 'Project', id) as unknown as Project
  return { ...row, visible_sections: parseVisibleSections(row.visible_sections) }
}

/**
 * Lista projetos acessíveis para um client_user (resumo leve).
 */
export async function listProjectsForClient(
  client: SupabaseClient,
  clientUserId: string,
): Promise<ProjectSummary[]> {
  const accesses = await listAccessibleProjectIds(client, clientUserId)
  if (accesses.length === 0) return []

  const projectIds = accesses.map((a) => a.project_id)
  const result = await client
    .schema('operacoes')
    .from('projects')
    .select(SUMMARY_COLUMNS)
    .in('id', projectIds)
    .order('updated_at', { ascending: false })

  return unwrapList(result) as unknown as ProjectSummary[]
}

/**
 * Lista membros de um projeto via RPC (combina dados de project_members + usuarios).
 * O RPC `get_project_members` aplica as joins necessárias respeitando RLS.
 */
export async function listProjectMembers(
  client: SupabaseClient,
  projectId: number,
): Promise<Array<Record<string, unknown>>> {
  const { data, error } = await client.schema('portal').rpc('get_project_members', {
    p_project_id: projectId,
  })
  if (error) {
    // RPC pode não existir em todos os ambientes — fallback para lista vazia
    return []
  }
  return (data ?? []) as Array<Record<string, unknown>>
}
